import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Edit2, Percent, Award, Package, Check, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import UsageTip from "@/components/UsageTip";

const emptyRange = { nombre: "", tipo: "range_based", tiers_rango: [
  { desde_pct_rango: 66, comision_pct: 10 },  // cerca del sugerido
  { desde_pct_rango: 33, comision_pct: 7 },   // medio
  { desde_pct_rango: 0,  comision_pct: 5 },   // cerca del mínimo
], tiers: [], comision_pct: 0, activa: true, motivo: "" };

const emptyTiered = { nombre: "", tipo: "tiered", tiers: [
  { desde_pct_sugerido: 100, comision_pct: 10 },
  { desde_pct_sugerido: 95, comision_pct: 7 },
  { desde_pct_sugerido: 0, comision_pct: 5 },
], tiers_rango: [], comision_pct: 0, activa: true, motivo: "" };

const emptyFlat = { nombre: "", tipo: "flat", tiers: [], tiers_rango: [], comision_pct: 6, activa: true, motivo: "" };

export default function ComisionesPage() {
  const [items, setItems] = useState([]);
  const [productos, setProductos] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyTiered);
  const [editing, setEditing] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignRegla, setAssignRegla] = useState(null);
  const [selectedProds, setSelectedProds] = useState(new Set());
  const [assignQ, setAssignQ] = useState("");
  const [assignFiltroCat, setAssignFiltroCat] = useState("");
  const [assignFiltroRegla, setAssignFiltroRegla] = useState(""); // "" = todas · id de regla · "default"

  // Categorías únicas del catálogo
  const categorias = Array.from(new Set(productos.map((p) => p.categoria).filter(Boolean))).sort();

  // Lista filtrada según buscador + categoría + regla actual
  const productosFiltrados = productos.filter((p) => {
    const q = assignQ.trim().toLowerCase();
    if (q) {
      const hay = (p.nombre || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q) || (p.categoria || "").toLowerCase().includes(q);
      if (!hay) return false;
    }
    if (assignFiltroCat && (p.categoria || "") !== assignFiltroCat) return false;
    if (assignFiltroRegla) {
      const rc = p.regla_comision_id || "default";
      if (rc !== assignFiltroRegla) return false;
    }
    return true;
  });

  const load = async () => {
    const [r, p] = await Promise.all([
      api.get("/reglas-comision"),
      api.get("/productos"),
    ]);
    setItems(r.data);
    setProductos(p.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        nombre: form.nombre,
        tipo: form.tipo,
        tiers: form.tipo === "tiered" ? form.tiers.map((t) => ({ desde_pct_sugerido: Number(t.desde_pct_sugerido), comision_pct: Number(t.comision_pct) })) : [],
        tiers_rango: form.tipo === "range_based" ? (form.tiers_rango || []).map((t) => ({ desde_pct_rango: Number(t.desde_pct_rango), comision_pct: Number(t.comision_pct) })) : [],
        comision_pct: form.tipo === "flat" ? Number(form.comision_pct) : null,
        activa: !!form.activa,
      };
      if (editing) {
        payload.motivo = form.motivo || "Actualización manual";
        await api.patch(`/reglas-comision/${editing.id}`, payload);
        toast.success("Regla actualizada");
      } else {
        await api.post("/reglas-comision", payload);
        toast.success("Regla creada");
      }
      setOpen(false); setForm(emptyRange); setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
  };

  const startEdit = (r) => {
    setEditing(r);
    setForm({
      nombre: r.nombre || "",
      tipo: r.tipo,
      tiers: r.tiers?.length ? r.tiers.map((t) => ({ ...t })) : [{ desde_pct_sugerido: 100, comision_pct: 10 }],
      tiers_rango: r.tiers_rango?.length ? r.tiers_rango.map((t) => ({ ...t })) : [
        { desde_pct_rango: 66, comision_pct: 10 },
        { desde_pct_rango: 33, comision_pct: 7 },
        { desde_pct_rango: 0, comision_pct: 5 },
      ],
      comision_pct: r.comision_pct ?? 0,
      activa: r.activa !== false,
      motivo: "",
    });
    setOpen(true);
  };

  const del = async (r) => {
    const usados = productos.filter((p) => (p.regla_comision_id || "default") === r.id).length;
    let force = false;
    if (usados > 0) {
      const ok = window.confirm(
        `La regla "${r.nombre}" está siendo usada por ${usados} producto(s).\n\n` +
        `Si continúas, esos productos quedarán SIN regla de comisión y aparecerán ` +
        `con una alerta hasta que les asignes una.\n\n¿Eliminar de todas formas?`,
      );
      if (!ok) return;
      force = true;
    } else if (!window.confirm(`Eliminar regla "${r.nombre}"?`)) return;
    try {
      const url = force ? `/reglas-comision/${r.id}?force=true` : `/reglas-comision/${r.id}`;
      const { data } = await api.delete(url);
      const n = data?.productos_desvinculados || 0;
      if (n > 0) toast.success(`Regla eliminada · ${n} producto(s) quedaron sin regla`);
      else toast.success("Regla eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const addTier = () => setForm({ ...form, tiers: [...form.tiers, { desde_pct_sugerido: 0, comision_pct: 0 }] });
  const rmTier = (i) => setForm({ ...form, tiers: form.tiers.filter((_, ix) => ix !== i) });
  const updTier = (i, patch) => setForm({ ...form, tiers: form.tiers.map((t, ix) => ix === i ? { ...t, ...patch } : t) });

  const addTierRango = () => setForm({ ...form, tiers_rango: [...(form.tiers_rango || []), { desde_pct_rango: 0, comision_pct: 0 }] });
  const rmTierRango = (i) => setForm({ ...form, tiers_rango: (form.tiers_rango || []).filter((_, ix) => ix !== i) });
  const updTierRango = (i, patch) => setForm({ ...form, tiers_rango: (form.tiers_rango || []).map((t, ix) => ix === i ? { ...t, ...patch } : t) });

  return (
    <div>
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Motor comercial</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2">Reglas de comisión</h1>
          <p className="text-muted-foreground mt-2">Reglas configurables sin tocar código. Cada venta congela la comisión con la regla vigente.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyRange); setOpen(true); }} data-testid="new-regla-btn" className="bg-primary hover:bg-primary/90 h-11">
          <Plus className="w-4 h-4 mr-2" /> Nueva regla
        </Button>
      </header>

      <UsageTip storageKey="comisiones" steps={[
        "Reglas por <b>rango de precio</b> (5% cerca del mínimo · 7% en el medio · 10% cerca del sugerido).",
        "También hay reglas <b>escalonadas</b> (según % del sugerido) o <b>fijas</b> (mismo % siempre).",
        "Usa <b>Asignar productos</b> para aplicar la regla en batch.",
      ]} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((r) => {
          const productosCount = productos.filter((p) => (p.regla_comision_id || "default") === r.id).length;
          return (
          <div key={r.id} className="bg-card border border-border rounded-xl p-6 card-hover" data-testid={`regla-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  <div className="font-display text-lg font-semibold text-foreground">{r.nombre}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wide">
                  {r.tipo === "range_based" ? "Por rango (mín→sug)" : r.tipo === "tiered" ? "Escalonada" : "Fija"}
                  {!r.activa ? " · inactiva" : ""}
                </div>
              </div>
              {(
                <div className="flex gap-1">
                  <button onClick={() => startEdit(r)} className="p-2 rounded-md hover:bg-muted text-muted-foreground/70 hover:text-foreground/80" title="Editar">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(r)} className="p-2 rounded-md hover:bg-rose-50 text-muted-foreground/70 hover:text-rose-600" title={r.id === 'default' ? 'Eliminar la regla predeterminada (los productos quedarán sin regla)' : 'Eliminar'} data-testid={`regla-del-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="mt-4 space-y-2">
              {r.tipo === "flat" && (
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-display font-semibold text-primary tabular-nums">{r.comision_pct}%</span>
                  <span className="text-xs text-muted-foreground">de comisión, sin importar el precio</span>
                </div>
              )}
              {r.tipo === "tiered" && (r.tiers || []).map((t, i) => (
                <div key={i} className="flex justify-between items-center text-sm border-b border-border/60 last:border-0 py-1.5">
                  <span className="text-muted-foreground">≥ {t.desde_pct_sugerido}% del sugerido</span>
                  <span className="font-semibold tabular-nums text-primary">{t.comision_pct}%</span>
                </div>
              ))}
              {r.tipo === "range_based" && (r.tiers_rango || []).map((t, i) => {
                const label = t.desde_pct_rango >= 66 ? "cerca del sugerido" : t.desde_pct_rango >= 33 ? "medio" : "cerca del mínimo";
                return (
                  <div key={i} className="flex justify-between items-center text-sm border-b border-border/60 last:border-0 py-1.5">
                    <span className="text-muted-foreground">≥ {t.desde_pct_rango}% del rango <span className="text-[10px] uppercase tracking-wide opacity-70">({label})</span></span>
                    <span className="font-semibold tabular-nums text-primary">{t.comision_pct}%</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-border/60 flex items-center justify-between">
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Package className="w-3 h-3" /> {productosCount} producto{productosCount === 1 ? "" : "s"} usan esta regla
              </div>
              <button onClick={() => {
                setAssignRegla(r);
                setSelectedProds(new Set(productos.filter((p) => (p.regla_comision_id || "default") === r.id).map((p) => p.id)));
                setAssignQ(""); setAssignFiltroCat(""); setAssignFiltroRegla("");
                setAssignOpen(true);
              }} className="text-xs text-primary hover:underline font-medium" data-testid={`assign-${r.id}`}>
                Asignar productos →
              </button>
            </div>
          </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyRange); } }}>
        <DialogContent className="max-w-2xl" data-testid="regla-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Editar regla" : "Nueva regla de comisión"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-4">
            <div>
              <Label>Nombre*</Label>
              <Input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="h-11 mt-1" placeholder="Ej: Cinta satinada · escalonada" data-testid="regla-nombre" />
            </div>
            <div>
              <Label>Tipo de regla</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <button type="button" onClick={() => setForm({ ...form, tipo: "range_based" })} className={`h-11 rounded-md border text-sm ${form.tipo === "range_based" ? "border-primary bg-primary text-primary-foreground" : "border-border"}`} data-testid="tipo-range">Por rango mín→sug</button>
                <button type="button" onClick={() => setForm({ ...form, tipo: "tiered" })} className={`h-11 rounded-md border text-sm ${form.tipo === "tiered" ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>Escalonada</button>
                <button type="button" onClick={() => setForm({ ...form, tipo: "flat" })} className={`h-11 rounded-md border text-sm ${form.tipo === "flat" ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>Fija (%)</button>
              </div>
              {form.tipo === "range_based" && (
                <p className="text-xs text-muted-foreground mt-2">La comisión depende de qué tan cerca del sugerido vendió el vendedor. 0% del rango = vendió al mínimo, 100% = vendió al sugerido.</p>
              )}
            </div>
            {form.tipo === "flat" && (
              <div>
                <Label>Comisión fija (%)</Label>
                <Input type="number" step="0.1" min="0" max="100" value={form.comision_pct} onChange={(e) => setForm({ ...form, comision_pct: e.target.value })} className="h-11 mt-1" data-testid="regla-flat-pct" />
              </div>
            )}
            {form.tipo === "range_based" && (
              <div>
                <div className="flex justify-between items-center">
                  <Label>Escalones por rango</Label>
                  <button type="button" onClick={addTierRango} className="text-xs text-primary hover:underline">+ Agregar escalón</button>
                </div>
                <div className="mt-2 space-y-2 p-3 bg-muted/40 rounded-lg">
                  <div className="grid grid-cols-12 gap-2 text-[11px] font-bold uppercase text-muted-foreground px-1">
                    <div className="col-span-6">Si vendió ≥ % del rango [mín, sug]</div>
                    <div className="col-span-5">→ Comisión</div>
                    <div className="col-span-1"></div>
                  </div>
                  {(form.tiers_rango || []).map((t, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-6 flex items-center gap-1">
                        <Input type="number" step="1" min="0" max="200" value={t.desde_pct_rango} onChange={(e) => updTierRango(i, { desde_pct_rango: e.target.value })} className="h-9 tabular-nums" data-testid={`tier-rango-pct-${i}`} />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                      <div className="col-span-5 flex items-center gap-1">
                        <Input type="number" step="0.1" min="0" max="100" value={t.comision_pct} onChange={(e) => updTierRango(i, { comision_pct: e.target.value })} className="h-9 tabular-nums" data-testid={`tier-rango-com-${i}`} />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                      <div className="col-span-1 text-right">
                        <button type="button" onClick={() => rmTierRango(i)} className="p-1 text-muted-foreground/70 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Ejemplo con mín=$10.000 y sug=$15.000: si vende a $14.000 → posición = 80% del rango → aplica el escalón ≥66%.</p>
              </div>
            )}
            {form.tipo === "tiered" && (
              <div>
                <div className="flex justify-between items-center">
                  <Label>Escalones</Label>
                  <button type="button" onClick={addTier} className="text-xs text-primary hover:underline">+ Agregar escalón</button>
                </div>
                <div className="mt-2 space-y-2 p-3 bg-muted/40 rounded-lg">
                  <div className="grid grid-cols-12 gap-2 text-[11px] font-bold uppercase text-muted-foreground px-1">
                    <div className="col-span-6">Si el precio ≥ % del sugerido</div>
                    <div className="col-span-5">→ Comisión</div>
                    <div className="col-span-1"></div>
                  </div>
                  {form.tiers.map((t, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-6 flex items-center gap-1">
                        <Input type="number" step="0.1" min="0" max="200" value={t.desde_pct_sugerido} onChange={(e) => updTier(i, { desde_pct_sugerido: e.target.value })} className="h-9 tabular-nums" data-testid={`tier-pct-${i}`} />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                      <div className="col-span-5 flex items-center gap-1">
                        <Input type="number" step="0.1" min="0" max="100" value={t.comision_pct} onChange={(e) => updTier(i, { comision_pct: e.target.value })} className="h-9 tabular-nums" data-testid={`tier-com-${i}`} />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                      <div className="col-span-1 text-right">
                        <button type="button" onClick={() => rmTier(i)} className="p-1 text-muted-foreground/70 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Se evalúan de mayor a menor umbral. El sistema aplica el primero que coincida.</p>
              </div>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.activa} onChange={(e) => setForm({ ...form, activa: e.target.checked })} className="w-4 h-4" />
              <span className="text-sm">Regla activa</span>
            </label>
            {editing && (
              <div>
                <Label>Motivo del cambio</Label>
                <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="h-11 mt-1" placeholder="Se registrará en el historial y en la notificación al equipo" />
              </div>
            )}
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90 h-11" data-testid="regla-save">{editing ? "Guardar cambios" : "Crear regla"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal: Asignar productos a una regla */}
      <Dialog open={assignOpen} onOpenChange={(v) => { setAssignOpen(v); if (!v) setAssignRegla(null); }}>
        <DialogContent className="max-w-3xl h-[85vh] p-0 overflow-hidden flex flex-col" data-testid="assign-modal">
          <div className="px-6 pt-6 pb-3 border-b border-border/60">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Asignar productos a "{assignRegla?.nombre}"</DialogTitle>
              <p className="text-sm text-muted-foreground">Selecciona uno, varios o todos los productos del catálogo que deben usar esta regla de comisión.</p>
            </DialogHeader>

            {/* Filtros */}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                <Input
                  placeholder="Buscar por nombre, SKU o categoría..."
                  value={assignQ}
                  onChange={(e) => setAssignQ(e.target.value)}
                  className="pl-9 h-10"
                  data-testid="assign-search"
                />
              </div>
              <select value={assignFiltroCat} onChange={(e) => setAssignFiltroCat(e.target.value)}
                className="h-10 border border-border rounded-md px-3 bg-card text-sm" data-testid="assign-filter-cat">
                <option value="">Todas las categorías</option>
                {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={assignFiltroRegla} onChange={(e) => setAssignFiltroRegla(e.target.value)}
                className="h-10 border border-border rounded-md px-3 bg-card text-sm" data-testid="assign-filter-regla">
                <option value="">Cualquier regla actual</option>
                {items.map((r) => <option key={r.id} value={r.id}>Actualmente en: {r.nombre}</option>)}
              </select>
            </div>

            {/* Acciones de selección */}
            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground flex-wrap gap-2">
              <div>
                <b className="text-foreground tabular-nums">{selectedProds.size}</b> seleccionado(s) · Mostrando {productosFiltrados.length} de {productos.length}
              </div>
              <div className="flex items-center gap-2">
                <button className="text-primary hover:underline" data-testid="assign-select-visible"
                  onClick={() => {
                    const next = new Set(selectedProds);
                    productosFiltrados.forEach((p) => next.add(p.id));
                    setSelectedProds(next);
                  }}>Seleccionar visibles</button>
                <span>·</span>
                <button className="text-primary hover:underline" data-testid="assign-select-all"
                  onClick={() => setSelectedProds(new Set(productos.map((p) => p.id)))}>Todos</button>
                <span>·</span>
                <button className="text-muted-foreground hover:underline" data-testid="assign-select-none"
                  onClick={() => setSelectedProds(new Set())}>Ninguno</button>
              </div>
            </div>
          </div>

          {/* Lista scrolleable (ocupa todo el espacio disponible entre header y footer) */}
          <div className="flex-1 overflow-y-auto divide-y divide-border/60 px-6" data-testid="assign-list">
            {productosFiltrados.length === 0 && (
              <div className="p-8 text-center text-muted-foreground/70 text-sm">
                {productos.length === 0 ? "No hay productos en el catálogo." : "No hay productos que coincidan con estos filtros."}
              </div>
            )}
            {productosFiltrados.map((p) => {
              const checked = selectedProds.has(p.id);
              const currentRule = p.regla_comision_id || "default";
              const differentRule = currentRule !== assignRegla?.id;
              const currentRuleName = currentRule === "default" ? "regla por defecto" : (items.find((r) => r.id === currentRule)?.nombre || "otra");
              return (
                <label key={p.id} className={`flex items-center gap-3 py-2.5 cursor-pointer hover:bg-muted/40 rounded-md px-2 -mx-2 ${checked ? "bg-accent" : ""}`} data-testid={`assign-prod-${p.id}`}>
                  <input type="checkbox" checked={checked} onChange={(e) => {
                    const next = new Set(selectedProds);
                    if (e.target.checked) next.add(p.id); else next.delete(p.id);
                    setSelectedProds(next);
                  }} className="w-4 h-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{p.nombre}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {p.sku && <span className="font-mono mr-2">{p.sku}</span>}
                      {p.categoria && <span className="mr-2">· {p.categoria}</span>}
                      <span>Sugerido: {(p.precio_sugerido || p.precio_base || 0).toLocaleString("es-CO")} COP</span>
                    </div>
                  </div>
                  <div className="text-right text-[10px] flex-shrink-0">
                    {differentRule ? (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase tracking-wide font-semibold">{currentRuleName}</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary uppercase tracking-wide font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> ya en esta regla</span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          {/* Footer STICKY — siempre visible con el botón de confirmación */}
          <div className="border-t border-border bg-card px-6 py-4 flex items-center justify-between gap-3 shadow-lg" data-testid="assign-footer">
            <div className="text-xs text-muted-foreground">
              <div>Los pedidos ya emitidos conservan su comisión original.</div>
              <div className="mt-0.5"><b className="text-foreground tabular-nums">{selectedProds.size}</b> producto{selectedProds.size === 1 ? "" : "s"} listo{selectedProds.size === 1 ? "" : "s"} para aplicar</div>
            </div>
            <Button
              disabled={selectedProds.size === 0}
              onClick={async () => {
                try {
                  const { data } = await api.post("/productos/_/asignar-regla", {
                    producto_ids: Array.from(selectedProds),
                    regla_comision_id: assignRegla.id,
                  });
                  toast.success(`${data.afectados} producto(s) actualizados`);
                  setAssignOpen(false);
                  load();
                } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo asignar"); }
              }}
              className="bg-primary hover:bg-primary/90 h-12 min-w-[240px] text-base font-semibold shadow-md" data-testid="assign-save">
              <Check className="w-5 h-5 mr-2" />
              {selectedProds.size === 0 ? "Selecciona productos" : `Confirmar ${selectedProds.size} producto${selectedProds.size === 1 ? "" : "s"}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
